import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateCta from "./UpdateCta";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewCta = ({ onWebinarCtaUpdated }) => {
  const [ctas, setCtas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCta, setEditingCta] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    description: "",
    button_text: "",
    button_link: ""
  });

  const fetchCtas = async () => {
    try {
      const response = await axios.get(`${apiUrl}/ctas`);
      setCtas(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load CTAs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCtas();
  }, []);

  const handleEdit = (cta) => {
    setEditingCta(cta.id);
    setFormData({
      slug: cta.slug,
      title: cta.title,
      description: cta.description,
      button_text: cta.button_text,
      button_link: cta.button_link
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/ctasupdate/${editingCta}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onWebinarCtaUpdated) {
        onWebinarCtaUpdated();
      }
      await fetchCtas();
      setEditingCta(null);
      alert("CTA updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the CTA.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this CTA?")) return;
    
    try {
      await axios.delete(`${apiUrl}/ctadelete/${id}`);
      setCtas(ctas.filter(cta => cta.id !== id));
      alert("CTA deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete CTA");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading CTAs...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Call To Actions</h2>
      </div>

      {ctas.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No CTAs found. Create your first CTA.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Button Text</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ctas.map((cta) => (
                <tr key={cta.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{cta.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">{cta.title}</div>
                    <div className="text-sm text-gray-500 mt-1">{cta.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a 
                      href={cta.button_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      {cta.button_text}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(cta)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(cta.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCta && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateCta
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingCta(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCta;